import { Ref, ref, unref, watch, computed } from 'vue'
import { v4 as uuid } from 'uuid'
import type { AdvancedSorting, Filters, Pagination, Sorting } from './general'
import dataStorageManager, { BucketDefaultIDs } from '../DataStorageManager'

export interface conditionFilter<T> {
  name: string
  id: number
  match: (item: T) => boolean
}

export interface ComplexBucketListOptions<T> {
  pagination?: Ref<Pagination>
  sorting?: Ref<AdvancedSorting<T>>
  filters?: Ref<Filters<T>>
  onStart?: () => void // Before first load
  onBeforeLoad?: () => void // Before every load
  onFirstLoad?: () => void // Only on first load
  onAfterLoad?: () => void // After every load
}

const makePaginationRef = () => ref<Pagination>({ page: 1, perPage: 10, total: 0 })
const makeSortingRef = <T>() => ref<AdvancedSorting<T>>({ sortFunction: (a, b) => 0, sortingOrder: 0 })
// El tipo usado en sorting se volvera un generico (T). Listar keyof T y por defecto sera el primero

const makeFiltersRef = <T>() => ref<Filters<T>>({ checker: () => true, search: '' })

export const useComplexBucketList = <T, O>(
  bucketID: BucketDefaultIDs,
  modTunnel: (item: O) => T,
  options?: ComplexBucketListOptions<T>,
) => {
  if (options && options.onStart) {
    options.onStart()
  }

  const recalcShowList = ref(uuid()) // Forzar recalc de showList
  const lastRecalc = ref(recalcShowList.value)
  const firstLoadDone = ref(false)
  const dataChanged = ref(false)
  const filterChanged = ref(false)
  const statusFilterChanged = ref(false)
  const sortingChanged = ref(false)
  const paginationChanged = ref(false)
  const someChanged = computed(() => {
    return (
      lastRecalc.value != recalcShowList.value ||
      dataChanged.value ||
      filterChanged.value ||
      statusFilterChanged.value ||
      sortingChanged.value ||
      paginationChanged.value
    )
  })
  const allUpdated = () => {
    dataChanged.value = false
    filterChanged.value = false
    statusFilterChanged.value = false
    sortingChanged.value = false
    paginationChanged.value = false
    lastRecalc.value = recalcShowList.value
  }
  const isLoading = ref(true)
  const error = ref()
  const bucketSubscription = dataStorageManager.subscribeBucket<O>(bucketID, (data) => {
    console.log('Received data', data)
    isLoading.value = false
    dataChanged.value = true
    if (options && options.onAfterLoad) {
      options.onAfterLoad()
    }
  })
  const lastMappedData: Ref<T[]> = ref([])
  const lastFilteredData: Ref<T[]> = ref([])
  const updateList = () => {
    lastMappedData.value = bucketSubscription.value.map(modTunnel)
    lastFilteredData.value = lastMappedData.value
      .filter((a: T) => {
        return statusFilter.value.match(a)
      })
      .filter((a: T) => filters.value.checker(a, filters.value.search))
    allUpdated()
  }
  const getMappedData = () => {
    if (someChanged.value) {
      updateList()
    }
    return lastMappedData.value
  }

  const getFilteredData = () => {
    if (someChanged.value) {
      updateList()
    }
    return lastFilteredData.value
  }

  const { filters = makeFiltersRef(), sorting = makeSortingRef(), pagination = makePaginationRef() } = options || {}
  const statusFilter = ref<conditionFilter<T>>({
    name: 'all',
    id: 0,
    match: (a: T) => true,
  })

  const fetch = async () => {
    pagination.value.total = getFilteredData().length
  }

  watch(
    filters,
    () => {
      if (pagination.value.page == 0 || pagination.value.page > pagination.value.total / pagination.value.perPage) {
        pagination.value.page = 1
      }
      filterChanged.value = true
      recalcShowList.value = uuid()
      fetch()
    },
    { deep: true },
  )

  watch(
    bucketSubscription,
    (newVal) => {
      if (pagination.value.page == 0 || pagination.value.page > pagination.value.total / pagination.value.perPage) {
        pagination.value.page = 1
      }
      dataChanged.value = true
      recalcShowList.value = uuid()
      fetch()
    },
    { deep: true },
  )

  watch(statusFilter, () => {
    if (pagination.value.page == 0 || pagination.value.page > pagination.value.total / pagination.value.perPage) {
      pagination.value.page = 1
    }
    statusFilterChanged.value = true
    recalcShowList.value = uuid()
    fetch()
  })

  fetch()

  const resultList = computed(() => {
    recalcShowList.value // Usado para forzar recalc
    const fulldata = getFilteredData()
    return fulldata
  })

  const showList = computed(() => {
    recalcShowList.value // Usado para forzar recalc

    // Leemos sorting FUERA del callback de .sort() para que el computed siempre
    // registre estas dependencias. Si el array filtrado tiene 0 o 1 elementos,
    // .sort() no invoca el comparador y, leyendo dentro, perdíamos la dependencia
    // reactiva de sorting → el orden por columna quedaba "bloqueado".
    const sortFunction = sorting.value.sortFunction
    const sortingOrder = sorting.value.sortingOrder

    // Copia para no mutar el array cacheado (lastFilteredData) in-place.
    const paginated = getFilteredData().slice()

    paginated.sort((a, b) => sortFunction(a, b) * sortingOrder)

    fetch()
    return paginated.slice(
      (pagination.value.page - 1) * pagination.value.perPage,
      pagination.value.page * pagination.value.perPage,
    )
  })

  const fullList = computed(() => {
    const fulldata = getMappedData()
    if (!firstLoadDone.value && fulldata.length > 0) {
      firstLoadDone.value = true
      if (options && options.onFirstLoad) {
        options.onFirstLoad()
      }
    }
    return fulldata
  })

  return {
    error: error,
    isLoading: isLoading,
    filters: filters,
    statusFilter: statusFilter,
    sorting: sorting,
    pagination: pagination,

    reload: recalcShowList,

    resultList,
    showList,
    fullList,

    fetch,

    async performAction(action: string, data: any) {
      isLoading.value = true
      try {
        // Propagamos el resultado de la operación (p. ej. el id generado por un insert)
        // para que el caller no dependa de re-buscar el registro en el bucket.
        return await dataStorageManager.performOperation(bucketID, action, data)
      } catch (e) {
        error.value = e
      } finally {
        isLoading.value = false
      }
    },
  }
}
